import { z } from 'zod';
import { DateString, RecordAccount, Timestamp, Uuid } from './common.js';
import { PROSE_STATEMENT_MAX_LENGTH } from './inputs.js';
import { ImportPhase } from './import.js';
import { DISPLAY_NAME_MAX_LENGTH, DISPLAY_NAME_TOO_LONG } from './knowledge.js';
import { RunSummary } from './runs.js';
import { SubjectStatus } from './vocab.js';

/**
 * **Events as subjects** — `/orgs/:orgId/events`
 * (`docs/notes/2026-09-23-event-graph-design.md`).
 *
 * An event subject is an `entity(kind='event')` with a `subject_record` and no
 * status row, the organisation pattern. This file is the generic event record;
 * it names no vertical, no analysis frame and no report type.
 */

/**
 * The record fields the create form writes. Dates and place are the office's
 * own statement about the event, stored in `subject_record.record_fields` under
 * these keys with the usual attribution; research may state them too, as facts.
 */
export const EVENT_RECORD_FIELDS = {
  startsOn: 'starts_on',
  endsOn: 'ends_on',
  place: 'place',
} as const;

/**
 * `POST /orgs/:orgId/events` — **New event**. Creating an event spends nothing:
 * no run is started and the "About this event" prose is filed unread, for event
 * research to take as the office's context.
 *
 * `entity_id` and `force_new` answer `NAME_CONFLICT` exactly as they do on the
 * person and organisation doors: track this event already on file, or mint a
 * different one that shares the name.
 */
export const CreateEventBody = z
  .object({
    name: z.string().trim().min(1).max(DISPLAY_NAME_MAX_LENGTH, DISPLAY_NAME_TOO_LONG),
    starts_on: DateString.nullable(),
    ends_on: DateString.nullable(),
    place: z.string().trim().max(200).nullable(),
    /** "About this event": host, speakers, topic, in the office's words. */
    about: z.string().trim().max(PROSE_STATEMENT_MAX_LENGTH).nullable(),
    entity_id: Uuid.optional(),
    force_new: z.literal(true).optional(),
    /** Required in a security org, refused in every other (server-vetted). */
    account_id: Uuid.optional(),
  })
  .strict()
  .refine((body) => !(body.entity_id !== undefined && body.force_new), {
    message: 'entity_id and force_new are mutually exclusive',
    path: ['force_new'],
  })
  .refine((body) => !body.starts_on || !body.ends_on || body.ends_on >= body.starts_on, {
    message: 'The end date is before the start date.',
    path: ['ends_on'],
  });
export type CreateEventBody = z.infer<typeof CreateEventBody>;

/** One tracked event on the Events list. */
export const EventListEntry = z
  .object({
    id: Uuid,
    display_name: z.string(),
    starts_on: DateString.nullable(),
    ends_on: DateString.nullable(),
    place: z.string().nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
    account: RecordAccount.nullable(),
  })
  .strict();
export type EventListEntry = z.infer<typeof EventListEntry>;

export const ListEventsQuery = z
  .object({
    q: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().optional(),
    account_id: Uuid.optional(),
  })
  .strict();
export type ListEventsQuery = z.infer<typeof ListEventsQuery>;
export type ListEventsQueryInput = z.input<typeof ListEventsQuery>;

export const EventListResponse = z
  .object({
    entries: z.array(EventListEntry),
    next_cursor: z.string().nullable(),
  })
  .strict();
export type EventListResponse = z.infer<typeof EventListResponse>;

/** The head of the event page. */
export const EventSubject = z
  .object({
    entity_id: Uuid,
    display_name: z.string(),
    kind: z.literal('event'),
    tracked: z.literal(true),
    connections: z.number().int().nonnegative(),
    created_at: Timestamp,
    updated_at: Timestamp,
    starts_on: DateString.nullable(),
    ends_on: DateString.nullable(),
    place: z.string().nullable(),
    account: RecordAccount.nullable(),
  })
  .strict();
export type EventSubject = z.infer<typeof EventSubject>;

/** What the office wrote about the event, newest first. */
export const EventStatement = z
  .object({
    id: Uuid,
    prose: z.string(),
    created_at: Timestamp,
  })
  .strict();
export type EventStatement = z.infer<typeof EventStatement>;

export const EventSummaryText = z
  .object({
    text: z.string(),
    updated_at: Timestamp,
  })
  .strict();
export type EventSummaryText = z.infer<typeof EventSummaryText>;

/** The particulars an event profile may state beside its description. */
export const EVENT_PROFILE_PARTICULARS = [
  'starts_on',
  'ends_on',
  'place',
  'venue',
  'format',
  'expected_size',
  'website',
] as const;

/**
 * The current `event_profile`: what the event's own pages say it is, with any
 * particular those pages leave empty filled from another reading of the event
 * (Soroush, 2026-09-24). `from_press` names the particulars filled from press
 * rather than from the event's own pages.
 */
export const EventProfile = z
  .object({
    description: z.string(),
    starts_on: z.string().nullable(),
    ends_on: z.string().nullable(),
    place: z.string().nullable(),
    venue: z.string().nullable(),
    format: z.string().nullable(),
    expected_size: z.string().nullable(),
    website: z.string().nullable(),
    from_press: z.array(z.enum(EVENT_PROFILE_PARTICULARS)),
  })
  .strict();
export type EventProfile = z.infer<typeof EventProfile>;

/** `GET /orgs/:orgId/events/:id` — the event page's Overview. */
export const EventRecord = z
  .object({
    subject: EventSubject,
    /** The current `event_profile`, or null until research has read one. */
    profile: EventProfile.nullable(),
    /** The current `event_summary`, or null until research has written one. */
    summary: EventSummaryText.nullable(),
    statements: z.array(EventStatement),
    runs: z.array(RunSummary),
  })
  .strict();
export type EventRecord = z.infer<typeof EventRecord>;

export const CreateEventResponse = EventRecord;
export type CreateEventResponse = z.infer<typeof CreateEventResponse>;

/** `DELETE /orgs/:orgId/events/:id` — stop tracking; the event node stays. */
export const UntrackEventResponse = z
  .object({
    untracked: z.literal(true),
    display_name: z.string(),
  })
  .strict();
export type UntrackEventResponse = z.infer<typeof UntrackEventResponse>;

/* ————————————————————————— attendees (the event graph) ————————————————————————— */

export const EVENT_ATTENDEE_STATUSES = ['pending', 'scanned', 'failed', 'promoted'] as const;
export const EventAttendeeStatus = z.enum(EVENT_ATTENDEE_STATUSES);
export type EventAttendeeStatus = z.infer<typeof EventAttendeeStatus>;

/**
 * **A frame's key on the wire**: a key of the analyses registry
 * (`event-frames.ts`). A string, as a tier key is: the server answers with the
 * frames the org's analyses have and refuses a frame the org does not ask, so
 * the wire does not repeat the registry's list.
 */
const FrameKey = z.string().min(1).max(40);

/**
 * **An attendee's tier on one of the vertical's analysis frames**
 * (`event-frames.ts`), or null when that frame has not rated them.
 */
export const EventAttendeeFrameTier = z
  .object({ frame: FrameKey, tier: z.string().nullable() })
  .strict();
export type EventAttendeeFrameTier = z.infer<typeof EventAttendeeFrameTier>;

/**
 * A live person on the record an attendee points at, and their status in the
 * org's vertical when tracked (null for a known person, who has none).
 */
export const EventAttendeeRecord = z
  .object({
    entity_id: Uuid,
    display_name: z.string(),
    tracked: z.boolean(),
    status: SubjectStatus.nullable(),
  })
  .strict();
export type EventAttendeeRecord = z.infer<typeof EventAttendeeRecord>;

/**
 * **One node of the event's graph** as the Attendees tab lists it. What the
 * list supplied, whether it matched somebody on the record, and how far the
 * scan got. Nothing here is a fact about a person.
 */
export const EventAttendee = z
  .object({
    id: Uuid,
    kind: z.enum(['person', 'org']),
    name: z.string(),
    status: EventAttendeeStatus,
    /** What the list supplied: email, phone, employer, title, city… */
    identifiers: z.record(z.string(), z.string()),
    /**
     * A person on the record this attendee matched by an identifier, or null:
     * followed through merges, and null once that person is deleted.
     */
    linked: EventAttendeeRecord.nullable(),
    /** The record this attendee was promoted into, or null. */
    promoted_entity_id: Uuid.nullable(),
    /**
     * The one record the attendee's row names and opens: the person promoted
     * into when promoted, else `linked`. Resolved like `linked`.
     */
    on_record: EventAttendeeRecord.nullable(),
    /** How many readings of the name the last scan found. */
    candidates: z.number().int().nonnegative(),
    findings: z.number().int().nonnegative(),
    /**
     * Somebody must say who this attendee is. For a vertical with analysis
     * frames: a reading carries something a frame rated, and nobody has chosen
     * or dismissed the readings yet (design ruling 7). For one without: the
     * scan found more than one person this name could be.
     */
    needs_review: z.boolean(),
    /**
     * Rated from the profile of the person on the record the attendee leads to,
     * not searched by name (the event analysis flow spec): that reading is
     * their tier on every frame while it stands.
     */
    on_record_rated: z.boolean(),
    /** The attendee's tier on each of the vertical's frames, in registry order. */
    frames: z.array(EventAttendeeFrameTier),
  })
  .strict();
export type EventAttendee = z.infer<typeof EventAttendee>;

/** One passage a scan found under an attendee's name, cited. */
export const EventAttendeeFinding = z
  .object({
    id: Uuid,
    quote: z.string(),
    point: z.string(),
    url: z.string().nullable(),
  })
  .strict();
export type EventAttendeeFinding = z.infer<typeof EventAttendeeFinding>;

/** How a frame rated one reading: its tier, why, and the findings it rests on. */
export const EventAttendeeAssessment = z
  .object({
    frame: FrameKey,
    tier: z.string(),
    reason: z.string(),
    finding_ids: z.array(Uuid),
  })
  .strict();
export type EventAttendeeAssessment = z.infer<typeof EventAttendeeAssessment>;

/**
 * **One reading of who the name could be.** `binding` is `confirmed` only when
 * a cited source states an identifier the list supplied; never identity.
 * `on_record` is the reading rated from the profile of the person on the record
 * the attendee already leads to; it is not a reading of the name to choose.
 */
export const EventAttendeeCandidate = z
  .object({
    id: Uuid,
    label: z.string(),
    binding: z.enum(['confirmed', 'name_only', 'on_record']),
    findings: z.array(EventAttendeeFinding),
    assessments: z.array(EventAttendeeAssessment),
  })
  .strict();
export type EventAttendeeCandidate = z.infer<typeof EventAttendeeCandidate>;

/** `GET /orgs/:orgId/events/:id/attendees/:nodeId` — one attendee, with the scan's readings. */
export const EventAttendeeDetail = z
  .object({
    attendee: EventAttendee,
    candidates: z.array(EventAttendeeCandidate),
    /**
     * The reading the office said is this attendee, or null. A pick is the
     * event's answer only: nothing goes on the record until the attendee is
     * added, and adding them carries the pick.
     */
    picked_candidate_id: Uuid.nullable(),
  })
  .strict();
export type EventAttendeeDetail = z.infer<typeof EventAttendeeDetail>;

/**
 * `POST …/attendees/:nodeId/promote` — **add this attendee to the records**
 * (event graph design §4). `candidate_id` is the reading the office chose, if
 * any; its findings come across as reference text. `entity_id` is a person
 * already on the record the office says this is; absent, the node's link is
 * used, or a new person is made.
 */
export const PromoteAttendeeBody = z
  .object({
    candidate_id: Uuid.nullable(),
    entity_id: Uuid.nullable(),
    /**
     * The answer to `NAME_CONFLICT`: a person of this name is on the record and
     * this attendee is somebody else. Otherwise a live namesake is asked about,
     * as every create door asks.
     */
    force_new: z.literal(true).optional(),
  })
  .strict()
  .refine((body) => !(body.entity_id && body.force_new), {
    message: 'entity_id and force_new are mutually exclusive',
    path: ['force_new'],
  });
export type PromoteAttendeeBody = z.infer<typeof PromoteAttendeeBody>;

export const PromoteAttendeeResponse = z
  .object({
    entity_id: Uuid,
    /** A new person was made, rather than an existing one used. */
    created: z.boolean(),
  })
  .strict();
export type PromoteAttendeeResponse = z.infer<typeof PromoteAttendeeResponse>;

/**
 * `POST /orgs/:orgId/events/:id/attendees/people` — **people added by hand**:
 * each recorded as an attendee on the record and listed on the attendee list.
 */
export const AddEventPeopleBody = z.object({ entity_ids: z.array(Uuid).min(1).max(100) }).strict();
export type AddEventPeopleBody = z.infer<typeof AddEventPeopleBody>;

export const AddEventPeopleResponse = z
  .object({
    added: z.number().int().nonnegative(),
    /** Already attending: nothing written for them. */
    already: z.number().int().nonnegative(),
  })
  .strict();
export type AddEventPeopleResponse = z.infer<typeof AddEventPeopleResponse>;

/** `GET /orgs/:orgId/events/:id/people` — everyone on the record attending. */
export const EventPeopleResponse = z
  .object({ people: z.array(z.object({ entity_id: Uuid, display_name: z.string() }).strict()) })
  .strict();
export type EventPeopleResponse = z.infer<typeof EventPeopleResponse>;

/**
 * `POST …/attendees/:nodeId/pick` — **this reading is the attendee, for this
 * event**: their ratings follow it alone and they leave the review list.
 * `DELETE` takes the pick back. Neither touches the record.
 */
export const PickAttendeeReadingBody = z.object({ candidate_id: Uuid }).strict();
export type PickAttendeeReadingBody = z.infer<typeof PickAttendeeReadingBody>;
export const PickAttendeeReadingResponse = z
  .object({ picked_candidate_id: Uuid.nullable() })
  .strict();
export type PickAttendeeReadingResponse = z.infer<typeof PickAttendeeReadingResponse>;

/** `POST …/attendees/:nodeId/dismiss` — none of the scan's readings is this attendee. */
export const DismissAttendeeResponse = z.object({ dismissed: z.literal(true) }).strict();
export type DismissAttendeeResponse = z.infer<typeof DismissAttendeeResponse>;

export const ListEventAttendeesQuery = z
  .object({
    q: z.string().trim().max(120).optional(),
    status: EventAttendeeStatus.optional(),
    /** Only attendees somebody must still say who they are (`needs_review`). */
    review: z.enum(['true']).optional(),
    /** Only attendees at `tier` on `frame` (`not_assessed` for none); both or neither. */
    frame: FrameKey.optional(),
    tier: z.string().trim().min(1).max(40).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    cursor: z.string().optional(),
  })
  .strict()
  .refine((query) => (query.frame === undefined) === (query.tier === undefined), {
    message: 'frame and tier go together',
    path: ['tier'],
  });
export type ListEventAttendeesQuery = z.infer<typeof ListEventAttendeesQuery>;

/** `GET /orgs/:orgId/events/:id/attendees` — the Attendees tab. */
export const EventAttendeesResponse = z
  .object({
    /** Counts over the whole graph, whatever the filter. */
    counts: z
      .object({
        total: z.number().int().nonnegative(),
        pending: z.number().int().nonnegative(),
        scanned: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        promoted: z.number().int().nonnegative(),
        linked: z.number().int().nonnegative(),
        needs_review: z.number().int().nonnegative(),
        /** Attendees the list gives a seat: the tables show a Seat column when any. */
        seated: z.number().int().nonnegative(),
      })
      .strict(),
    /** Per frame of the vertical: attendees at each tier, and those not rated. */
    frames: z.array(
      z
        .object({
          frame: FrameKey,
          counts: z.record(z.string(), z.number().int().nonnegative()),
          not_assessed: z.number().int().nonnegative(),
        })
        .strict(),
    ),
    attendees: z.array(EventAttendee),
    next_cursor: z.string().nullable(),
    /**
     * Uploads of a list for this event that have not finished: a session sits
     * at its plan until somebody reviews and imports it, and nothing reaches
     * the list before then. Oldest first.
     */
    open_uploads: z.array(
      z
        .object({
          import_id: Uuid,
          filename: z.string().min(1),
          phase: ImportPhase.nullable(),
        })
        .strict(),
    ),
    /**
     * The scan: how many a scan started now would search on the web
     * (`scannable`) and rate from their profile on the record (`record_rated`),
     * and the one going.
     */
    scan: z
      .object({
        scannable: z.number().int().nonnegative(),
        record_rated: z.number().int().nonnegative(),
        active_run_id: Uuid.nullable(),
        last_run_id: Uuid.nullable(),
        /**
         * Per analysis of the vertical that needs the scan: attendees already
         * scanned that a scan for it would rate from what is stored
         * (`rerate`), and its last scan, or null when it has never run. For an
         * analysis with a parameterized factor, also the input its ratings on
         * show were made against (`input`, null until one rated somebody) and
         * how many already scanned a different input would rate
         * (`rerate_new_input`); both are null for one with only fixed factors.
         */
        analyses: z.array(
          z
            .object({
              analysis: z.string(),
              rerate: z.number().int().nonnegative(),
              last_run_id: Uuid.nullable(),
              input: z.record(z.string(), z.string()).nullable(),
              rerate_new_input: z.number().int().nonnegative().nullable(),
              /**
               * Its latest scan completed and wrote no report: held while an
               * attendee was unmatched, until the office presses Write the report.
               */
              report_held: z.boolean(),
            })
            .strict(),
        ),
      })
      .strict(),
  })
  .strict();
export type EventAttendeesResponse = z.infer<typeof EventAttendeesResponse>;

/**
 * `POST /orgs/:orgId/events/:id/scan` — scan for one analysis of the org's
 * vertical, rating only its factors; `input` is what its parameterized factors
 * are rated against, one entry per input the analysis declares.
 */
export const StartEventScanBody = z
  .object({
    analysis: z.string().min(1).max(60),
    input: z.record(z.string().max(60), z.string().max(4000)).default({}),
    /**
     * The optional fields of the analysis's report type, asked with the scan
     * because the report is written when the scan completes (2026-10-03). They
     * steer the write-up only; nothing is rated against them.
     */
    report_inputs: z.record(z.string().min(1).max(64), z.string().max(20_000)).default({}),
    /** An enrolled account's press: a retried press with the same key gets the
     * scan the first admitted, and the price its button showed is the most the
     * scan may hold (credit-ledger.md, live charging). */
    admission_key: z.uuid().optional(),
    expected_credit_units: z.number().int().min(0).optional(),
  })
  .strict();
export type StartEventScanBody = z.infer<typeof StartEventScanBody>;

/** `POST /orgs/:orgId/events/:id/scan` — the scan it started. */
export const StartEventScanResponse = z.object({ run_id: Uuid }).strict();
export type StartEventScanResponse = z.infer<typeof StartEventScanResponse>;

/**
 * `DELETE /orgs/:orgId/events/:id/attendees` — **delete the attendee list**
 * (event graph design §3, after the event): every attendee and every page the
 * scans captured. People already added to the records stay there; the scan
 * runs stay as the record of what was spent.
 */
export const DeleteEventAttendeesResponse = z
  .object({
    removed_attendees: z.number().int().nonnegative(),
    removed_sources: z.number().int().nonnegative(),
  })
  .strict();
export type DeleteEventAttendeesResponse = z.infer<typeof DeleteEventAttendeesResponse>;
